import * as Telecom from '@servicetitan/telecom-rtcs-client';
import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import ServiceQueue from '../../types/ServiceQueue';
import log, { logException } from '../../lib/log';

// Maximum allowed difference (ms) between a session's enteredAt and the
// entry time computed from the event (timestamp - totalWaitMs).
// Accounts for integer-ms rounding in totalWaitMs and second-level precision
// in the event timestamp.
export const MAX_ENTRY_TIME_DIFF_MS = 5000;

// Returns the index of the session whose enteredAt is closest to targetMs
// and within maxDiffMs. Returns -1 if no session qualifies or on error.
export function findClosestSessionIndex(
  sessions: { enteredAt: Date }[],
  targetMs: number,
  maxDiffMs: number,
): number {
  try {
    let closestIndex = -1;
    let closestDiff = Infinity;

    for (let i = 0; i < sessions.length; i++) {
      const diff = Math.abs(sessions[i].enteredAt.getTime() - targetMs);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    return closestDiff <= maxDiffMs ? closestIndex : -1;
  } catch (err) {
    logException(
      err,
      'findClosestSessionIndex: failed to compute session time diff',
    );
    return -1;
  }
}

/**
 * Removes a session from the queue using the feature-flag-aware strategy.
 * Shared by handleQueueSessionLeft and handleQueueSessionDelivered.
 */
export function removeSessionFromQueue(params: {
  queue: ServiceQueue;
  event: Telecom.QueueSessionLeftEvent | Telecom.QueueSessionDeliveredEvent;
  logPrefix: string;
  handlerName: string;
}): void {
  const { queue, event, logPrefix, handlerName } = params;
  if (
    featureFlagModule.isEnabled(FeatureFlagKeys.featureShowQueueLongestWaitTime)
  ) {
    removeQueueSession({
      queue,
      conversationId: event.conversationId,
      timestamp: event.timestamp,
      totalWaitMs: event.totalWaitMs,
      activeSessions: event.stats.activeSessions,
      logPrefix,
      handlerName,
    });
  } else {
    queue.sessions = queue.sessions.filter(
      (s) => s.id !== event.conversationId,
    );
  }
}

export type RemoveSessionResult =
  | 'cleared_all'
  | 'removed_by_id'
  | 'removed_by_closest_time'
  | 'not_found';

/**
 * Removes a session from queue.sessions using a 4-step strategy:
 * 1. If activeSessions === 0, clear all sessions.
 * 2. Find session by conversationId and remove it.
 * 3. If not found by id, compute enteredAt from (timestamp - totalWaitMs)
 *    and remove the session with the closest enteredAt within MAX_ENTRY_TIME_DIFF_MS.
 * 4. If still not found, log an exception.
 *
 * Returns a result token so the caller can react (e.g. early-return on 'cleared_all').
 */
export function removeQueueSession(params: {
  queue: { id: string; sessions: { id: string; enteredAt: Date }[] };
  conversationId: string;
  timestamp: string;
  totalWaitMs: number;
  activeSessions: number;
  logPrefix: string;
  handlerName: string;
}): RemoveSessionResult {
  const {
    queue,
    conversationId,
    timestamp,
    totalWaitMs,
    activeSessions,
    logPrefix,
    handlerName,
  } = params;

  try {
    // Step 1: if no active sessions remain, clear all local sessions.
    if (activeSessions === 0) {
      log(`${logPrefix}:cleared_all`, { queueId: queue.id, activeSessions });
      queue.sessions = [];
      return 'cleared_all';
    }

    // Step 2: find by conversationId and remove.
    // Sessions that joined while the app was open have their id set to the RTCS conversationId.
    const idIndex = queue.sessions.findIndex((s) => s.id === conversationId);
    if (idIndex >= 0) {
      log(`${logPrefix}:removed_by_id`, { queueId: queue.id, conversationId });
      queue.sessions = [
        ...queue.sessions.slice(0, idIndex),
        ...queue.sessions.slice(idIndex + 1),
      ];
      return 'removed_by_id';
    }

    // Step 3: id not found — session was pre-loaded from the API before a join event arrived,
    // so its id is a GraphQL UUID that won't match the RTCS conversationId.
    // Reconstruct the entry time as: timestamp - totalWaitMs ≈ joinedAtTime,
    // then remove the session with the closest enteredAt within the allowed threshold.
    const computedEnteredAt = new Date(timestamp).getTime() - totalWaitMs;
    const closestIndex = findClosestSessionIndex(
      queue.sessions,
      computedEnteredAt,
      MAX_ENTRY_TIME_DIFF_MS,
    );
    if (closestIndex >= 0) {
      const enteredAtDiffMs =
        queue.sessions[closestIndex].enteredAt.getTime() - computedEnteredAt;
      log(`${logPrefix}:removed_by_closest_time`, {
        queueId: queue.id,
        conversationId,
        computedEnteredAt,
        closestIndex,
        enteredAtDiffMs,
      });
      queue.sessions = [
        ...queue.sessions.slice(0, closestIndex),
        ...queue.sessions.slice(closestIndex + 1),
      ];
      return 'removed_by_closest_time';
    }

    // Step 4: could not identify the session — log and move on.
    logException(
      new Error(
        `${handlerName}: could not find session to remove — queueId: ${queue.id}, conversationId: ${conversationId}, computedEnteredAt: ${computedEnteredAt}`,
      ),
      `${logPrefix}:not_found`,
    );
    return 'not_found';
  } catch (err) {
    logException(err, `${logPrefix}:removeQueueSession failed`);
    return 'not_found';
  }
}
